// src/auth/refreshCoordinator.ts

import type { AxiosError } from 'axios'
import { logger } from '@myorg/type-lib'

/**
 * Queue 中等待 refresh 完成的請求
 */
interface QueueItem {
  resolve: () => void
  reject: (error: AxiosError | Error) => void
}

/**
 * 全域唯一 refresh 協調器
 */
class RefreshCoordinator {
  /**
   * 是否正在 refresh
   */
  private isRefreshing = false

  /**
   * 等待 refresh 的請求
   */
  private queue: QueueItem[] = []

  /**
   * logout single-flight
   */
  private logoutPromise: Promise<void> | null = null

  /**
   * 是否正在 refresh
   */
  get refreshing() {
    return this.isRefreshing
  }

  /**
   * 嘗試取得 refresh 鎖
   *
   * return:
   * true  -> 呼叫者負責 refresh
   * false -> 已有其他人 refresh 中
   */
  beginRefresh(): boolean {
    if (this.isRefreshing) {
      return false
    }

    this.isRefreshing = true

    logger.log(
      '[RefreshCoordinator] 🔑 acquire refresh lock'
    )

    return true
  }

  /**
   * 將請求加入等待佇列
   */
  waitForRefresh(): Promise<void> {
    logger.log(
      `[RefreshCoordinator] ⏳ queue request (size=${this.queue.length + 1})`
    )

    return new Promise((resolve, reject) => {
      this.queue.push({
        resolve,
        reject
      })
    })
  }

  /**
   * refresh 成功
   *
   * 解除鎖並放行所有等待請求
   */
  resolveRefresh() {
    logger.log(
      `[RefreshCoordinator] ✅ refresh success, release ${this.queue.length} request(s)`
    )

    const queue = this.queue

    this.queue = []

    /**
     * ★ 關鍵：
     * 先解鎖，再放行 queue
     *
     * 避免：
     *
     * processQueue()
     * ↓
     * 新 401 進 queue
     * ↓
     * finally unlock
     * ↓
     * queue 永遠卡死
     */
    this.isRefreshing = false

    queue.forEach(item => {
      item.resolve()
    })
  }

  /**
   * refresh 失敗
   *
   * 解除鎖並拒絕所有等待請求
   */
  rejectRefresh(error: AxiosError | Error) {
    logger.error(
      `[RefreshCoordinator] 💥 refresh failed, reject ${this.queue.length} request(s)`
    )

    const queue = this.queue

    this.queue = []

    /**
     * ★ 同樣先解鎖
     */
    this.isRefreshing = false

    queue.forEach(item => {
      item.reject(error)
    })
  }

  /**
   * logout single-flight
   *
   * 多個地方同時 logout：
   *
   * Router Guard
   * refresh fail
   * User click logout
   * checkAuth fail
   *
   * 都只會執行一次真正 logout
   */
  runLogout(
    executor: () => Promise<void>
  ): Promise<void> {
    if (this.logoutPromise) {
      logger.log(
        '[RefreshCoordinator] 🚪 reuse existing logout'
      )

      return this.logoutPromise
    }

    logger.log(
      '[RefreshCoordinator] 🚪 start logout'
    )

    this.logoutPromise = (async () => {
      try {
        await executor()
      } finally {
        this.logoutPromise = null

        logger.log(
          '[RefreshCoordinator] 🚪 logout completed'
        )
      }
    })()

    return this.logoutPromise
  }

  /**
   * 測試或除錯用途
   */
  reset() {
    this.isRefreshing = false
    this.queue = []
    this.logoutPromise = null

    logger.warn(
      '[RefreshCoordinator] ⚠️ coordinator reset'
    )
  }
}

/**
 * 全域唯一實例
 */
export const refreshCoordinator =
  new RefreshCoordinator()